// Current version of the database.
const DB_VERSION = 2;
// The name of the object store holding the events.
const EVENT_STORE_NAME = 'events';
// The name of the indexDB object store used to store key value pairs
const KEY_VAL_STORE = 'keyval';
// The path used to reference the timestamp when querying events ordered by timestamp.
const TIMESTAMP_EVENT_PATH = 'timestamp';
/**
 * Utility function to open an indexedDb database leveraging promises.
 * @param databaseName the name of the database.
 * @param version the version of the database.
 * @param onUpgradeCallBack the callback to be called when the database is created or upgraded
 *  add here the logic to create or upgrade indexedDb object stores.
 * @returns a promise that resolves to the indexedDb database instance.
 */
const openDb = async (databaseName, version, onUpgradeCallBack) => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(databaseName, version);
        request.onerror = () => {
            reject(request.error);
        };
        request.onsuccess = () => {
            resolve(request.result);
        };
        request.onupgradeneeded = () => {
            const db = request.result;
            onUpgradeCallBack(db);
        };
    });
};
/**
 * Update or insert a value in the object store.
 * @param db The indexedDb database instance.
 * @param objectStore The name of the object store to use.
 * @param key The key used to store the value.
 * @param value The value to store.
 * @returns a promise that resolves when the value is stored.
 */
const storeValue = async (db, objectStore, key, value) => {
    return new Promise((resolve, reject) => {
        const tx = db.transaction(objectStore, 'readwrite');
        const store = tx.objectStore(objectStore);
        const request = store.put(value, key);
        request.onerror = () => {
            reject(request.error);
        };
        request.onsuccess = () => {
            resolve();
        };
    });
};
const getValue = async (db, objectStore, key) => {
    return new Promise((resolve, reject) => {
        const tx = db.transaction(objectStore, 'readonly');
        const store = tx.objectStore(objectStore);
        const request = store.get(key);
        request.onerror = () => {
            reject(request.error);
        };
        request.onsuccess = () => {
            resolve(request.result);
        };
    });
};
/**
 * Return all the values from an object store querying an index.
 * @param db The indexedDb database instance.
 * @param objectStore The name of the object store to use.
 * @param index The name of the index to query.
 * @param limit The maximum number of values to return.
 * @param resultOrdering The ordering of the results.
 * @returns a promise that resolves to an array of values.
 */
const getAllFromIndex = async (db, objectStore, index, limit, resultOrdering) => {
    return new Promise((resolve, reject) => {
        const tx = db.transaction(objectStore, 'readonly');
        const store = tx.objectStore(objectStore);
        const request = store.index(index).openCursor(null, resultOrdering);
        const result = [];
        request.onerror = () => {
            reject(request.error);
        };
        request.onsuccess = () => {
            const cursor = request.result;
            if (cursor && result.length < limit) {
                result.push(cursor.value);
                cursor.continue();
            }
            else {
                resolve(result);
            }
        };
    });
};
/**
 * Count the number of values in an object store.
 * @param db The indexedDb database instance.
 * @param objectStore The name of the object store to use.
 * @returns a promise that resolves to the number of values in the object store.
 */
const countValues = async (db, objectStore) => {
    return new Promise((resolve, reject) => {
        const tx = db.transaction(objectStore, 'readonly');
        const store = tx.objectStore(objectStore);
        const request = store.count();
        request.onerror = () => {
            reject(request.error);
        };
        request.onsuccess = () => {
            resolve(request.result);
        };
    });
};
/**
 * Delete values from an object store.
 * @param db The indexedDb database instance.
 * @param objectStore The name of the object store to use.
 * @param keys The keys of the values to delete.
 * @returns a promise that resolves when the values are deleted.
 */
const deleteValues = async (db, objectStore, keys) => {
    return new Promise((resolve, reject) => {
        const tx = db.transaction(objectStore, 'readwrite');
        const store = tx.objectStore(objectStore);
        const promises = [];
        keys.forEach((key) => {
            const request = store.delete(key);
            promises.push(new Promise((resolve, reject) => {
                request.onerror = () => {
                    reject(request.error);
                };
                request.onsuccess = () => {
                    resolve();
                };
            }));
        });
        Promise.all(promises)
            .then(() => resolve())
            .catch(reject);
    });
};
export const indexedDBPersistence = async (config = {}) => {
    const fullConfig = {
        dbName: 'pico',
        maxEventsStored: 300,
        targetSizeAfterClearingSpace: 200,
        ...config,
    };
    const openDatabase = async () => {
        return openDb(fullConfig.dbName, DB_VERSION, (db) => {
            if (!db.objectStoreNames.contains(EVENT_STORE_NAME)) {
                const store = db.createObjectStore(EVENT_STORE_NAME);
                store.createIndex(TIMESTAMP_EVENT_PATH, 'timestamp');
            }
            if (!db.objectStoreNames.contains(KEY_VAL_STORE)) {
                db.createObjectStore(KEY_VAL_STORE);
            }
        });
    };
    let cachedDB = await openDatabase();
    const isOpen = (db) => {
        try {
            db.transaction([EVENT_STORE_NAME], 'readwrite');
            return true;
        }
        catch (err) {
            const error = err;
            return error.name !== 'InvalidStateError';
        }
    };
    const ensureConnection = async () => {
        if (cachedDB && isOpen(cachedDB))
            return cachedDB;
        cachedDB = await openDatabase();
        return cachedDB;
    };
    const deleteEvents = async (events) => {
        const keys = events.map((event) => event.id);
        const db = await ensureConnection();
        await deleteValues(db, EVENT_STORE_NAME, keys);
    };
    return {
        getValue: async (key) => {
            const db = await ensureConnection();
            const value = await getValue(db, KEY_VAL_STORE, key);
            return value;
        },
        saveValue: async (key, value) => {
            const db = await ensureConnection();
            await storeValue(db, KEY_VAL_STORE, key, value);
        },
        getEvents: async (limit) => {
            const db = await ensureConnection();
            const events = await getAllFromIndex(db, EVENT_STORE_NAME, TIMESTAMP_EVENT_PATH, limit, 'next');
            return events;
        },
        saveEvent: async (event) => {
            const db = await ensureConnection();
            const totalEvents = await countValues(db, EVENT_STORE_NAME);
            if (totalEvents >= fullConfig.maxEventsStored) {
                const eventsToDelete = totalEvents - fullConfig.targetSizeAfterClearingSpace;
                const events = await getAllFromIndex(db, EVENT_STORE_NAME, TIMESTAMP_EVENT_PATH, eventsToDelete, 'next');
                await deleteEvents(events);
            }
            await storeValue(db, EVENT_STORE_NAME, event.id, event);
        },
        countEvents: async () => {
            const db = await ensureConnection();
            const count = await countValues(db, EVENT_STORE_NAME);
            return count;
        },
        deleteEvents,
    };
};
//# sourceMappingURL=indexedDbPersistenceManager.js.map